import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent, FocusEvent, ReactNode, useState } from "react";

import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { hasValidationMessage } from "../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import ConditionalWrapper from "./ConditionalWrapper";
import sanitizeInput, { Sanitizers } from "./sanitizeInput";
import unobtrusiveValidation from "./unobtrusiveValidation";
import useFieldData from "./useFieldData";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

interface Props {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  inputValue?: string;
  id?: string;
  sanitize?: Sanitizers<string>;
  validators?: Validators<string>;
  onBlur?: (value: string) => void;
  onChange?: (value: string) => void;
  propertyName: string;
  excludeRow?: boolean;
  widthClassName?: string;
  capitalize?: boolean;
  required?: boolean;
}

export const TextArea = (props: Props) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, placeholder, prefix, suffix, info, tooltip } = useFieldData(props.fieldData, name);
  const [isDirty, setDirty] = useState(Boolean(props.inputValue));
  const modelState = props.modelState;

  const sanitizeAndValidateField = (input: string) => {
    const parsedInput = props.capitalize ? input.toUpperCase() : input;
    const sanitizedValue = sanitizeInput(parsedInput, props.sanitize);
    let dirty = isDirty;
    if (!dirty && sanitizedValue) {
      setDirty(true);
      dirty = true;
    }

    if (dirty && props.setModelState) {
      props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, sanitizedValue));
    }

    return sanitizedValue;
  };

  const onFieldBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    if (!props.onBlur) {
      return;
    }

    const validatedValue = sanitizeAndValidateField(e.target.value);

    e.target.value = validatedValue;

    props.onBlur(validatedValue);
  };

  const onFieldChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!props.onChange) {
      return;
    }

    const validatedValue = sanitizeAndValidateField(e.target.value);

    e.target.value = validatedValue;

    props.onChange(validatedValue);
  };

  const id = props.id || fieldName;
  const errorId = `${id}-error`;
  const hasError = hasValidationMessage(modelState, props.propertyName);

  return (
    <ConditionalWrapper
      condition={!props.excludeRow}
      wrap={(children: ReactNode) => <div className="form-row">{children}</div>}
    >
      <div className={`form-item ${props.widthClassName}`}>
        <div className="form-input">
          {heading.value && (
            <div className="form-label">
              <label htmlFor={id}>{heading.value}</label>
              <Tooltip fieldName={fieldName} value={tooltip.value} />
            </div>
          )}
          {prefix.value && <div className="prefix">{prefix.value}</div>}
          <textarea
            required={props.required}
            placeholder={placeholder.value}
            autoComplete="off"
            name={fieldName}
            id={id}
            defaultValue={props.inputValue}
            onBlur={onFieldBlur}
            onChange={onFieldChange}
            aria-invalid={hasError}
            aria-errormessage={hasError ? errorId : undefined}
          />
          {suffix.value && <div className="suffix">{suffix.value}</div>}
        </div>
        {info.value && <span>{info.value}</span>}
      </div>
      <ValidationMessage modelState={modelState} property={props.propertyName} id={errorId} />
    </ConditionalWrapper>
  );
};
